import React, { useState, useEffect } from 'react';
import { MapPinIcon, UserIcon, ClockIcon, PhoneIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/apiService';
import PageHeader from '../components/ui/PageHeader';
import Badge, { BadgeTone } from '../components/ui/Badge';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';

const TECH_STATUS_TONE: Record<string, BadgeTone> = {
  online: 'info',
  available: 'success',
  busy: 'warning',
  offline: 'neutral',
};

interface TechnicianLocation {
  id: string;
  technicianId: string;
  technicianName: string;
  phoneNumber?: string;
  latitude: number;
  longitude: number;
  address?: string;
  status: 'online' | 'offline' | 'available' | 'busy';
  lastSeen: string;
  currentAssignment?: string;
}

const TechnicianLocations: React.FC = () => {
  const [filter, setFilter] = useState<'all' | 'online' | 'offline' | 'available' | 'busy'>('all');

  // Fetch technician locations from backend
  const { data: techniciansData, isLoading, error, refetch } = useQuery({
    queryKey: ['technician-locations'],
    queryFn: async () => {
      const response = await apiService.get('/technician-locations');
      if (response.success && response.data) {
        // Map backend response to frontend interface
        return response.data.map((item: any) => ({
          id: item.id,
          technicianId: item.userId,
          technicianName: `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Unknown',
          phoneNumber: item.phone || 'N/A',
          latitude: item.latitude || 0,
          longitude: item.longitude || 0,
          address: item.metadata?.address || 'Location not available',
          status: item.status || 'offline',
          lastSeen: item.lastUpdated || new Date().toISOString(),
          currentAssignment: item.metadata?.currentAssignment
        }));
      }
      return [];
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  const technicians = (techniciansData || []) as TechnicianLocation[];

  const filteredTechnicians = technicians.filter(tech => {
    if (filter === 'all') return true;
    if (filter === 'online') return tech.status === 'online' || tech.status === 'available';
    if (filter === 'offline') return tech.status === 'offline';
    return tech.status === filter;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <p className="ml-4 text-gray-600 dark:text-gray-400">Loading technician locations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">
          <p>Error loading technician locations. Please try again.</p>
          <Button variant="primary" className="mt-4" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Technician Locations"
        subtitle="Track real-time locations and status of field technicians across the municipality."
      />

      {/* Filter Controls */}
      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Filter by Status</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="border border-gray-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Technicians</option>
              <option value="online">Online</option>
              <option value="available">Available</option>
              <option value="busy">Busy</option>
              <option value="offline">Offline</option>
            </select>
          </div>

          <div className="ml-auto flex items-center gap-4">
            <Button variant="primary" icon={ArrowPathIcon} onClick={() => refetch()}>
              Refresh
            </Button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Total: {filteredTechnicians.length} technicians
            </span>
          </div>
        </div>
      </div>

      {/* Technicians Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTechnicians.map((technician) => (
          <div key={technician.id} className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <UserIcon className="h-6 w-6 text-primary-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{technician.technicianName}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">ID: {technician.technicianId}</p>
                </div>
              </div>
              <Badge tone={TECH_STATUS_TONE[technician.status] ?? 'neutral'}>{technician.status}</Badge>
            </div>

            <div className="space-y-3">
              {technician.phoneNumber && technician.phoneNumber !== 'N/A' && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <PhoneIcon className="h-4 w-4" />
                  <span>{technician.phoneNumber}</span>
                </div>
              )}
              
              {technician.address && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <MapPinIcon className="h-4 w-4" />
                  <span className="truncate">{technician.address}</span>
                </div>
              )}
              
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <ClockIcon className="h-4 w-4" />
                <span>Last seen: {technician.lastSeen ? new Date(technician.lastSeen).toLocaleString() : 'N/A'}</span>
              </div>

              {technician.currentAssignment && (
                <div className="mt-3 p-3 bg-primary-50 rounded-sm">
                  <p className="text-sm font-medium text-primary-900">Current Assignment</p>
                  <p className="text-sm text-primary-700">{technician.currentAssignment}</p>
                </div>
              )}
            </div>

            {technician.latitude && technician.longitude && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Latitude:</span>
                    <p className="font-mono">{technician.latitude.toFixed(6)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Longitude:</span>
                    <p className="font-mono">{technician.longitude.toFixed(6)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredTechnicians.length === 0 && (
        <EmptyState icon={MapPinIcon} title="No technicians found" description="No technicians match the selected filter." />
      )}
    </div>
  );
};

export default TechnicianLocations;





